import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '@environments/environment';
import type { SelectItem } from '@radancy-pe/ui-elements/select';
import { CompanyService } from '@app/shared/services/company.service';
import { EFBUserRole, IFBPagedResponseHttp, IFBUserInfo, IFBUserStatistics } from '@interfaces';
import { IFBUsersQuery, IFBUserStatus } from '@app/models/user';
import { featureFlags } from '@utils/feature-flags';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class UserResource {
  constructor(
    private companyService: CompanyService,
    private http: HttpClient
  ) {}

  private get companyId(): string {
    return this.companyService.data.id;
  }

  confirmEmailChange(id: string, body: { token: string }) {
    const url = `${this.url()}/${id}/email/change_confirmations`;
    return this.http.post<void>(url, body);
  }

  findCompany(recaptchaToken: string, email: string) {
    const headers = recaptchaToken ? new HttpHeaders({ 'X-Recaptcha-Token': recaptchaToken }) : {};
    if (featureFlags.isOn('DomainReminderUsesQueryParam')) {
      const url = `https://${environment.API_BASE}/v1/users/domains`;
      return this.http.post<void>(url, '', { headers, params: { email } });
    } else {
      const url = `https://${environment.API_BASE}/v1/users/${email}/domains`;
      return this.http.post<void>(url, '', { headers });
    }
  }

  get(params: IFBUsersQuery) {
    return this.http.get<IFBPagedResponseHttp<IFBUserInfo>>(this.url(), { params });
  }

  getForSelectItem(params: IFBUsersQuery): Observable<SelectItem[]> {
    return this.get(params).pipe(
      map(response =>
        response.items.map(user => ({
          id: user.id,
          label: `${user.firstName} ${user.lastName}`
        }))
      )
    );
  }

  getById(userId: string) {
    return this.http.get<IFBUserInfo>(this.url() + '/' + userId);
  }

  getPoints(userId: string) {
    return this.http.get<{ totalPoints: number }>(this.url() + `/${userId}/points`);
  }

  getStatistics(userId: string) {
    return this.http.get<IFBUserStatistics>(`${this.url()}/${userId}/statistics`);
  }

  updateProfilePicture(userId: string, imageId: any) {
    const url = `${this.url()}/${userId}/profile_picture`;
    return this.http.put<void>(url, { image: { id: imageId } });
  }

  deleteProfilePicture(userId: string) {
    const url = `${this.url()}/${userId}/profile_picture`;
    return this.http.delete<void>(url);
  }

  updateProfile(userId: string, user: any) {
    return this.http.put<void>(`${this.url()}/${userId}/profile`, user);
  }

  saveRole(userId: string, role: EFBUserRole) {
    return this.http.post<IFBUserInfo>(`${this.url()}/${userId}/roles`, {
      companyId: this.companyId,
      id: userId,
      value: role
    });
  }

  updateStatus(userId: string, status: IFBUserStatus) {
    const payload = { id: userId, status, companyId: this.companyId };
    return this.http.put<IFBUserInfo>(`${this.url()}/${userId}/status`, payload);
  }

  delete(userId: string) {
    return this.http.delete<void>(`${this.url()}/${userId}`);
  }

  disableMFA(userId: string, mfaDeviceId: string) {
    const url = `https://${environment.AUTH_BASE}/api/accounts/${userId}/devices/${mfaDeviceId}`;
    const options = { headers: { 'Content-Type': 'application/json;charset=utf-8' }, body: {} };
    return this.http.delete(url, options);
  }

  setEmail(userId: string, emailToken: string, body: { email: string }) {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${emailToken}`
    });
    return this.http.post<void>(`${this.url()}/${userId}/email/change_requests`, body, { headers });
  }

  private url(): string {
    return `https://${environment.API_BASE}/v1/companies/${this.companyId}/users`;
  }
}
